import moment, { Moment } from 'moment';

export type DateRange = {
  startDate: Date | Moment | string;
  endDate: Date | Moment | string;
};

export type MomentDateRange = {
  startDate: Moment;
  endDate: Moment;
};

export const isBetween = function (range: any, date: any) {
  return (
    moment
      .utc(range.startDate, 'DD-MM-YYYY')
      .isSameOrBefore(moment.utc(date, 'DD-MM-YYYY')) &&
    moment
      .utc(range.endDate, 'DD-MM-YYYY')
      .isSameOrAfter(moment.utc(date, 'DD-MM-YYYY'))
  );
};

export const rangesOverlap = function (
  rangeOne: DateRange,
  rangeTwo: DateRange
) {
  return (
    isBetween(rangeOne, rangeTwo.startDate) ||
    isBetween(rangeOne, rangeTwo.endDate)
  );
};

export const mergeRanges = function (
  rangeOne: MomentDateRange,
  rangeTwo: MomentDateRange
) {
  let newRange: MomentDateRange = {
    startDate: moment.utc(),
    endDate: moment.utc()
  };

  if (isBetween(rangeOne, rangeTwo.startDate)) {
    newRange.startDate = rangeOne.startDate;
  } else {
    newRange.startDate = rangeTwo.startDate;
  }
  if (isBetween(rangeOne, rangeTwo.endDate)) {
    newRange.endDate = rangeOne.endDate;
  } else {
    newRange.endDate = rangeTwo.endDate;
  }

  return newRange;
};

export const merge = function (rangeCollection: DateRange[]) {
  let concatenatedCollections = rangeCollection
    .sort((a, b) =>
      moment
        .utc(a.startDate, 'DD-MM-YYYY')
        .diff(moment.utc(b.startDate, 'DD-MM-YYYY'))
    )
    .map<MomentDateRange>((range) => ({
      startDate: moment.utc(range.startDate, 'DD-MM-YYYY'),
      endDate: moment.utc(range.endDate, 'DD-MM-YYYY')
    }));

  let newCollection = concatenatedCollections.reduce<MomentDateRange[]>(
    (newCollection, range) => {
      let index = newCollection.findIndex((rangeToCheck) =>
        rangesOverlap(rangeToCheck, range)
      );
      if (index !== -1) {
        newCollection[index] = mergeRanges(newCollection[index], range);
      } else {
        newCollection.push(range);
      }
      return newCollection;
    },
    []
  );

  return newCollection;
};

export const historyType = (item: any) => {
  if (item?.employer) return 'employment';
  if (item?.education) return 'education';
  if (item?.selfEmployed) return 'self-employment';
  if (item?.unemployed) return 'unemployed';

  console.log('Unknown history type', item);
  throw new Error('Unknown history type');
};

export const getHistoryName = (item: any) =>
  item?.employer?.application?.name ||
  item?.education?.application?.name ||
  (item?.unemployed && 'Unemployed') ||
  item?.selfEmployed?.application?.accountantName ||
  item?.employer?.response?.orgName ||
  item?.education?.response?.orgName ||
  item?.selfEmployed?.response?.orgName ||
  '* Not given *';

// Extract gap calculation logic from Timeline component
export const calculateHistoryGaps = (
  history: readonly any[],
  timelineStartDate: Moment,
  yearsBack: number
) => {
  const today = timelineStartDate.clone();
  const yearsAgo = timelineStartDate.clone().subtract(yearsBack, 'years');

  type DateItem = {
    history: any | undefined;
    dates: DateRange[];
    unverified: boolean;
    over28Days: boolean;
    unknown: boolean;
  };

  // Process history dates similar to Timeline component
  const historyDates: DateItem[] = history
    ? [...history]
        .filter((item) => !item.isArchived)
        .map((item: any) => {
          let unverified = false;

          let responseDates =
            item?.employer?.response?.datesEmployed ??
            item?.education?.response?.dates ??
            item?.selfEmployed?.response?.dates ??
            item?.unemployed?.response?.alternateDates ??
            [];

          if (responseDates.length === 0) {
            unverified = true;

            if (
              historyType(item) === 'unemployed' &&
              item?.unemployed?.response?.datesAccurate
            ) {
              unverified = false;
            }

            responseDates =
              item?.employer?.application?.dates ??
              item?.education?.application?.dates ??
              item?.selfEmployed?.application?.dates ??
              item?.unemployed?.application?.dates ??
              [];
          }

          // Limit responseDates to timeline bounds
          const limitTime = (date: Date | Moment) => {
            if (moment.utc(date).isBefore(yearsAgo)) {
              return yearsAgo;
            }
            if (moment.utc(date).isAfter(today)) {
              return today;
            }
            return moment.utc(date);
          };

          const responseDatesFiltered = responseDates.map(
            ({
              startDate,
              endDate
            }: {
              startDate: Date | Moment;
              endDate: Date | Moment;
            }) => ({
              startDate: limitTime(startDate),
              endDate: limitTime(endDate)
            })
          );

          return {
            history: item,
            dates: responseDatesFiltered,
            unverified:
              unverified && historyType(item) === 'unemployed'
                ? false
                : unverified,
            over28Days: unverified && historyType(item) === 'unemployed',
            unknown: false
          };
        })
    : [];

  // Get a single list of all dates in historyDates.dates
  const dateList = historyDates.reduce((dateList, { dates }) => {
    return [...dateList, ...dates];
  }, [] as DateRange[]);

  const mergedDates = merge(dateList).reduce((dates: any[], date: any) => {
    return [...dates, date.startDate, date.endDate];
  }, []);

  const coveredDates = [yearsAgo, ...mergedDates, today];

  const gaps: DateItem[] = [];
  for (let i = 0; i < coveredDates.length; i++) {
    if (i % 2 === 0) continue;

    const start = coveredDates[i - 1];
    const end = coveredDates[i];

    const gapDays = moment(end).diff(moment(start), 'days');

    if (gapDays >= 28) {
      gaps.push({
        history: undefined,
        unverified: false,
        unknown: false,
        dates: [
          {
            startDate: start,
            endDate: end
          }
        ],
        over28Days: true
      });
    } else if (gapDays >= 1) {
      gaps.push({
        dates: [
          {
            startDate: start,
            endDate: end
          }
        ],
        unknown: true,
        history: undefined,
        unverified: false,
        over28Days: false
      });
    }
  }

  return {
    historyDates,
    gaps,
    hasLargeGaps: gaps.some((gap) => gap.over28Days),
    largeGaps: gaps.filter((gap) => gap.over28Days)
  };
};
